// frontend/src/pages/admin/CreateEventPage.tsx

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import type { EventStatus } from "../../types";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { getEventTutorialDefaults, TOTAL_TUTORIAL_STEPS } from "../../config/eventTutorialDefaults";
import {
  EventTutorialGuide,
  EventTutorialIntroBanner,
  getTutorialHighlightClass,
} from "../../components/admin/EventTutorialGuide";

function CheckInsideIcon() {
  return <Check className="w-3 h-3 text-white" />;
}

export default function CreateEventPage() {
  const { t } = useTranslation();
  const { artist, organization, platformCapabilities } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventId } = useParams();
  const isEditMode = !!eventId;

  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialGuideOpen, setTutorialGuideOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialChecked, setTutorialChecked] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    location: "",
    maxClients: "",
    avgTimePerClient: "60",
    status: "UPCOMING" as EventStatus,
    phoneRequired: true,
    emailNotifications: true,
    whatsappNotifications: false,
    pushNotifications: true,
    autoSendInvoice: false,
    defaultDeliveryMethod: "NONE",
    maxPartySize: "8",
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const [planLimitModal, setPlanLimitModal] = useState(false);

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => api.getEvent(eventId!),
    enabled: isEditMode,
  });

  const { data: integrations } = useQuery({
    queryKey: ["integrations", artist?.id],
    queryFn: () => api.getIntegrations(artist!.id),
    enabled: !!artist,
  });

  const { data: billingStatus } = useQuery({
    queryKey: ["billingStatus"],
    queryFn: () => api.getBillingStatus(),
  });
  const isFreePlan = (billingStatus?.plan ?? "FREE") !== "PRO";

  const hasEmailConfig =
    platformCapabilities.email || integrations?.channelStatus?.email === true;

  // WhatsApp désactivé sur plan FREE
  const hasWhatsappConfig =
    !isFreePlan &&
    (platformCapabilities.whatsapp || integrations?.channelStatus?.whatsapp === true);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      emailNotifications: hasEmailConfig ? prev.emailNotifications : false,
      whatsappNotifications: hasWhatsappConfig ? prev.whatsappNotifications : false,
      defaultDeliveryMethod:
        prev.defaultDeliveryMethod === "EMAIL" && !hasEmailConfig
          ? "NONE"
          : (prev.defaultDeliveryMethod === "WHATSAPP" ||
              prev.defaultDeliveryMethod === "SMS") &&
              !hasWhatsappConfig
            ? "NONE"
            : prev.defaultDeliveryMethod,
    }));
  }, [hasEmailConfig, hasWhatsappConfig]);

  const markTutorialComplete = useCallback(async (): Promise<void> => {
    if (!artist?.id) return;
    try {
      await api.updateBranding(artist.id, { eventTutorialCompleted: true });
    } catch {
      /* non-blocking */
    }
  }, [artist?.id]);

  useEffect(() => {
    if (isEditMode || !artist?.id || tutorialChecked) return;

    const initTutorial = async (): Promise<void> => {
      try {
        const branding = await api.getBranding(artist.id);
        const shouldRun =
          searchParams.get("tutorial") === "1" &&
          branding?.eventTutorialCompleted !== true;

        if (shouldRun) {
          setFormData(
            getEventTutorialDefaults(organization?.businessType ?? "OTHER", t, {
              emailNotifications: hasEmailConfig,
              whatsappNotifications: hasWhatsappConfig,
            }),
          );
          setTutorialActive(true);
          setTutorialGuideOpen(true);
        }
      } finally {
        setTutorialChecked(true);
      }
    };

    void initTutorial();
  }, [
    artist?.id,
    hasEmailConfig,
    hasWhatsappConfig,
    isEditMode,
    organization?.businessType,
    searchParams,
    t,
    tutorialChecked,
  ]);

  const handleTutorialSkip = async (): Promise<void> => {
    await markTutorialComplete();
    setTutorialActive(false);
    setTutorialGuideOpen(false);
    navigate("/admin/dashboard");
  };

  const handleTutorialNext = (): void => {
    if (tutorialStep >= TOTAL_TUTORIAL_STEPS - 1) {
      setTutorialGuideOpen(false);
      document
        .querySelector('[data-tutorial="submit"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setTutorialStep((s) => s + 1);
  };

  const handleTutorialBack = (): void => {
    setTutorialStep((s) => Math.max(0, s - 1));
  };

  const hl = (target: Parameters<typeof getTutorialHighlightClass>[2]): string =>
    getTutorialHighlightClass(tutorialActive && tutorialGuideOpen, tutorialStep, target);

  // Remplit le formulaire avec les données de l'événement
  useEffect(() => {
    if (event && isEditMode) {
      setFormData({
        name: event.name,
        description: event.description || "",
        startDate: new Date(event.startDate).toISOString().slice(0, 16),
        endDate: event.endDate
          ? new Date(event.endDate).toISOString().slice(0, 16)
          : "",
        location: event.location || "",
        maxClients: event.maxClients?.toString() || "",
        avgTimePerClient: event.avgTimePerClient?.toString() || "60",
        status: event.status,
        phoneRequired: event.phoneRequired ?? true,
        emailNotifications: event.emailNotifications ?? true,
        whatsappNotifications: event.whatsappNotifications ?? false,
        pushNotifications: event.pushNotifications ?? true,
        autoSendInvoice: event.autoSendInvoice ?? false,
        defaultDeliveryMethod: event.defaultDeliveryMethod || "NONE",
        maxPartySize: event.maxPartySize?.toString() || "8",
      });
    }
  }, [event, isEditMode]);

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: api.createEvent,
    onSuccess: async (event) => {
      if (tutorialActive) {
        await markTutorialComplete();
        setTutorialActive(false);
        setTutorialGuideOpen(false);
      }
      setModalState({
        isOpen: true,
        title: `✅ ${t("admin.event.createdTitle")}`,
        message: t("admin.event.createdMessage", { code: event.code }),
      });
      setTimeout(() => navigate("/admin/dashboard"), 2000);
    },
    onError: (error: any) => {
      if (error.response?.data?.code === "PLAN_LIMIT_REACHED") {
        setPlanLimitModal(true);
        return;
      }
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: error.response?.data?.error || t("admin.event.errorCreate"),
      });
    },
  });

  // Mutation pour modifier
  const updateMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: any }) =>
      api.updateEvent(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setModalState({
        isOpen: true,
        title: `✅ ${t("admin.event.updatedTitle")}`,
        message: t("admin.event.updatedMessage"),
      });
      setTimeout(() => navigate("/admin/dashboard"), 1500);
    },
    onError: (error: any) => {
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: error.response?.data?.error || t("admin.event.errorUpdate"),
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!artist) return;

    const startDate = new Date(formData.startDate);
    const resolvedStatus =
      startDate > new Date() && formData.status === "ACTIVE"
        ? ("UPCOMING" as EventStatus)
        : formData.status;

    const eventData = {
      artistId: artist.id,
      name: formData.name,
      description: formData.description || undefined,
      startDate: startDate.toISOString(),
      endDate: formData.endDate
        ? new Date(formData.endDate).toISOString()
        : undefined,
      location: formData.location || undefined,
      maxClients: formData.maxClients
        ? parseInt(formData.maxClients)
        : undefined,
      maxPartySize: formData.maxPartySize
        ? parseInt(formData.maxPartySize)
        : undefined,
      avgTimePerClient: parseInt(formData.avgTimePerClient),
      status: resolvedStatus,
      phoneRequired: formData.phoneRequired,
      emailNotifications: formData.emailNotifications,
      whatsappNotifications: formData.whatsappNotifications,
      pushNotifications: formData.pushNotifications,
      autoSendInvoice: formData.autoSendInvoice,
      defaultDeliveryMethod: formData.defaultDeliveryMethod,
    };

    if (isEditMode) {
      updateMutation.mutate({ eventId: eventId!, data: eventData });
    } else {
      createMutation.mutate(eventData);
    }
  };

  if (isEditMode && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-2xl text-black">
          ⏳ {t("common.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">

      {/* ── Modal Plan Limit ─────────────────────────────────── */}
      {planLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white border border-gray-200 max-w-md w-full p-8 flex flex-col gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {t("plan.limit.title", "Limite du plan Free atteinte")}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              {t(
                "plan.limit.eventMessage",
                "Vous profitez actuellement du plan Free, qui permet de créer 1 événement actif. Pour gérer plusieurs événements simultanément, passez à Pro.",
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
              <button
                type="button"
                onClick={() => setPlanLimitModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                {t("common.cancel")}
              </button>
              <Link
                to="/admin/billing"
                className="flex-1 px-4 py-2 bg-stone-900 hover:bg-stone-800 text-white font-medium text-center"
                onClick={() => setPlanLimitModal(false)}
              >
                {t("admin.billing.upgradeToPro", "Passer à Pro")}
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-3xl mx-auto ${tutorialGuideOpen ? "pb-44" : ""}`}>
        <div className="bg-white rounded-lg shadow p-8">
          <div className="mb-6">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="text-sky-700 hover:text-sky-800 mb-4"
            >
              ← {t("common.back")}
            </button>
            <h1 className="text-3xl font-bold">
              {isEditMode ? t("common.edit") : t("admin.dashboard.createEvent")}
            </h1>
          </div>

          {tutorialActive && tutorialGuideOpen && tutorialStep === 0 && (
            <EventTutorialIntroBanner />
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div
              data-tutorial="basics"
              className={`space-y-6 ${hl("basics")}`}
            >
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.nameLabel")}
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                placeholder={t("admin.event.namePlaceholder")}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.descLabel")}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                rows={3}
                placeholder={t("admin.event.descPlaceholder")}
              />
            </div>
            </div>

            <div
              data-tutorial="schedule"
              className={`space-y-6 ${hl("schedule")}`}
            >
            {/* Date de début */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.startDateLabel")}
              </label>
              <input
                type="datetime-local"
                required
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
              />
            </div>

            {/* Date de fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.endDateLabel")}
              </label>
              <input
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
              />
            </div>

            {/* Statut de l'événement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("common.status")}
              </label>
              <select
                required
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as EventStatus,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
              >
                <option value="UPCOMING">{t("status.upcoming")}</option>
                <option value="ACTIVE">{t("status.active")} ✅</option>
                <option value="PAUSED">{t("status.paused")}</option>
                <option value="CLOSED">{t("status.closed")}</option>
                <option value="COMPLETED">{t("status.completed")}</option>
                <option value="CANCELLED">{t("status.cancelled")}</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {t("admin.event.statusHelper")}
              </p>
            </div>
            </div>

            <div
              data-tutorial="details"
              className={`space-y-6 ${hl("details")}`}
            >
            {/* Lieu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.locationLabel")}
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                placeholder={t("admin.event.locationPlaceholder")}
              />
            </div>

            {/* Nombre max de clients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.maxClientsLabel")}
                {isFreePlan && (
                  <span className="ml-2 text-xs text-amber-600 font-semibold">({t("plan.free.maxClients", "max 30 — plan Free")})</span>
                )}
              </label>
              {isFreePlan ? (
                <select
                  value={formData.maxClients}
                  onChange={(e) => setFormData({ ...formData, maxClients: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                >
                  <option value="">{t("admin.event.noLimit", "Sans limite")}</option>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  min="1"
                  value={formData.maxClients}
                  onChange={(e) =>
                    setFormData({ ...formData, maxClients: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                  placeholder="100"
                />
              )}
            </div>

            {/* Taille max de table — uniquement pour les restaurants */}
            {organization?.businessType === "RESTAURANT" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👥 {t("admin.event.maxPartySizeLabel", "Taille max d'un groupe (personnes par table)")}
                </label>
                <select
                  value={formData.maxPartySize}
                  onChange={(e) => setFormData({ ...formData, maxPartySize: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 15, 20].map((n) => (
                    <option key={n} value={n}>{n} {t("admin.event.persons", "personnes")}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Durée moyenne par client */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.event.avgTimeLabel")}
              </label>
              <input
                type="number"
                min="15"
                max="480"
                required
                value={formData.avgTimePerClient}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    avgTimePerClient: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600"
                placeholder="60"
              />
              <p className="text-sm text-gray-500 mt-1">
                {t("admin.event.avgTimeHelper")}
              </p>
            </div>
            </div>

            {/* Téléphone obligatoire */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-start gap-4 cursor-pointer">
                <div className="flex h-6 items-center">
                  <input
                    type="checkbox"
                    checked={formData.phoneRequired}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        phoneRequired: e.target.checked,
                      })
                    }
                    className="h-5 w-5 rounded border-gray-300 text-sky-700 focus:ring-sky-600"
                  />
                </div>
                <div className="text-sm leading-6">
                  <span className="font-bold text-gray-900 flex items-center gap-2">
                    📱 {t("admin.event.phoneRequiredLabel")}
                  </span>
                  <p className="text-gray-500">
                    {formData.phoneRequired
                      ? t("admin.event.phoneRequiredHelper")
                      : t("admin.event.phoneOptionalHelper")}
                  </p>
                </div>
              </label>
            </div>

            {/* Facturation & Reçus */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold border-b pb-2 flex items-center gap-2 text-gray-700">
                📂 {t("common.billing")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.autoSendInvoice ? 'border-slate-900 bg-slate-50' : 'border-gray-200 bg-white'}`}>
                  <input
                    type="checkbox"
                    checked={formData.autoSendInvoice}
                    onChange={(e) => setFormData({ ...formData, autoSendInvoice: e.target.checked })}
                    className="w-5 h-5 text-sky-700 rounded focus:ring-sky-600"
                  />
                  <div>
                    <p className="text-sm font-semibold">{t("admin.event.autoSendInvoice")}</p>
                    <p className="text-xs text-gray-500">{t("admin.event.autoSendInvoiceHelper")}</p>
                  </div>
                </label>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t("admin.event.defaultMethod")}</label>
                  <select
                    value={formData.defaultDeliveryMethod}
                    onChange={(e) => setFormData({ ...formData, defaultDeliveryMethod: e.target.value })}
                    className="w-full p-2.5 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-600 transition-all bg-white font-medium"
                  >
                    <option value="NONE">{t("common.none")}</option>
                    {hasEmailConfig && <option value="EMAIL">Email</option>}
                    {hasWhatsappConfig && <option value="WHATSAPP">WhatsApp</option>}
                    {hasWhatsappConfig && <option value="SMS">SMS</option>}
                  </select>
                </div>
              </div>
            </div>

            {/* Modes de notification */}
            <div
              data-tutorial="notifications"
              className={`space-y-4 ${hl("notifications")}`}
            >
              <label className="block text-sm font-bold text-gray-700">
                🔔 {t("admin.event.notificationsLabel")}
              </label>
              <p className="text-xs text-gray-500 -mt-3 mb-2">
                {t("admin.event.notificationsHelper")}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Email */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all 
                    ${!hasEmailConfig ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    ${formData.emailNotifications ? 'border-slate-900 bg-slate-50' : 'border-gray-200 bg-white'}`}
                  title={!hasEmailConfig ? t("admin.event.emailConfigIncomplete") : ""}
                >
                  <input
                    type="checkbox"
                    checked={formData.emailNotifications}
                    disabled={!hasEmailConfig}
                    onChange={(e) => hasEmailConfig && setFormData({ ...formData, emailNotifications: e.target.checked })}
                    className="hidden"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center border ${formData.emailNotifications ? 'bg-slate-900 border-slate-900' : 'bg-white border-gray-300'}`}>
                    {formData.emailNotifications && <CheckInsideIcon />}
                  </div>
                  <span className="text-sm font-medium">📧 {t("common.email")}</span>
                </label>

                {/* WhatsApp */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all 
                    ${!hasWhatsappConfig ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    ${formData.whatsappNotifications ? 'border-green-600 bg-green-50' : 'border-gray-200 bg-white'}`}
                  title={
                    isFreePlan
                      ? t("admin.event.whatsappProOnly", "WhatsApp est disponible avec le plan Pro")
                      : !hasWhatsappConfig
                        ? t("admin.event.whatsappConfigIncomplete")
                        : ""
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.whatsappNotifications}
                    disabled={!hasWhatsappConfig}
                    onChange={(e) => hasWhatsappConfig && setFormData({ ...formData, whatsappNotifications: e.target.checked })}
                    className="hidden"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center border ${formData.whatsappNotifications ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300'}`}>
                    {formData.whatsappNotifications && <CheckInsideIcon />}
                  </div>
                  <span className="text-sm font-medium">💬 WhatsApp</span>
                  {isFreePlan && (
                    <span className="ml-auto text-xs font-medium border border-gray-300 text-gray-600 px-2 py-0.5">Pro</span>
                  )}
                </label>

                {/* Web Push */}
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${formData.pushNotifications ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                  <input
                    type="checkbox"
                    checked={formData.pushNotifications}
                    onChange={(e) => setFormData({ ...formData, pushNotifications: e.target.checked })}
                    className="hidden"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center border ${formData.pushNotifications ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}`}>
                    {formData.pushNotifications && <CheckInsideIcon />}
                  </div>
                  <span className="text-sm font-medium">📱 Web Push</span>
                </label>
              </div>
              
              {(!hasEmailConfig || !hasWhatsappConfig) && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  ⚠️ <strong>{t("common.attention") || "Attention :"}</strong> {t("admin.event.missingConfigWarning") || "Certains canaux (Email ou WhatsApp) sont désactivés car vous n'avez pas renseigné vos clés API (Twilio / Resend)."} 
                  <button type="button" onClick={() => navigate("/admin/integrations")} className="ml-1 underline font-semibold text-yellow-900">{t("common.configureNow") || "Configurer maintenant"}</button>
                </div>
              )}
            </div>

            {/* Bouton */}
            <div data-tutorial="submit" className={hl("submit")}>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 font-semibold"
            >
              {createMutation.isPending || updateMutation.isPending
                ? t("common.loading")
                : isEditMode
                  ? t("common.save")
                  : t("admin.dashboard.createEvent")}
            </button>
            </div>
          </form>
        </div>
      </div>

      {tutorialActive && tutorialGuideOpen && (
        <EventTutorialGuide
          step={tutorialStep}
          onNext={handleTutorialNext}
          onBack={handleTutorialBack}
          onSkip={handleTutorialSkip}
        />
      )}

      {/* 🆕 MODALE DE CONFIRMATION */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
}
